import type { AuthFetch } from '../properties/api';

export interface Workspace {
  workspaceId: string;
  kind: 'personal' | 'studio';
  name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  capabilities: { rename: boolean; clientGalleries: boolean; personalUploads: boolean };
  usage: { scope: 'account'; plan: string; storageBytes: number; storageQuotaBytes: number } | null;
  createdAt: string;
  updatedAt: string;
}

async function read<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = 'Could not load your workspaces. Please try again.';
    try { message = (await response.json()).message || message; } catch { /* Keep the fallback for a non-JSON response. */ }
    throw new Error(message);
  }
  return response.json();
}

export const bootstrapWorkspaces = async (authFetch: AuthFetch): Promise<Workspace[]> => {
  const result = await read<{ workspaces: Workspace[] }>(await authFetch('/workspaces/bootstrap', { method: 'POST' }));
  if (!Array.isArray(result.workspaces)) throw new Error('Could not load your workspaces. Please try again.');
  return result.workspaces;
};

export const renameWorkspace = async (authFetch: AuthFetch, workspaceId: string, name: string): Promise<Workspace> =>
  read<Workspace>(await authFetch(`/workspaces/${encodeURIComponent(workspaceId)}`, {
    method: 'PATCH', body: JSON.stringify({ name }),
  }));

// Existing URLs continue to select the current owner's studio. This bridge
// must be removed when media requests become workspace-scoped in Stage 2B.
export function workspaceForPath(workspaces: Workspace[], pathname: string): Workspace | undefined {
  const explicit = pathname.match(/^\/workspaces\/([^/]+)\/?$/);
  if (explicit) return workspaces.find(workspace => workspace.workspaceId === explicit[1]);
  if (/^\/(galleries|library|projects|properties)(\/|$)/.test(pathname)) {
    return workspaces.find(workspace => workspace.capabilities.clientGalleries);
  }
  if (pathname.startsWith('/admin')) return undefined;
  return workspaces.find(workspace => workspace.kind === 'personal');
}
